import type { ReactNode } from 'react';
import { NavLink } from 'react-router';
import type { LucideIcon } from 'lucide-react';

type FirstRunStateProps = {
  icon: LucideIcon;
  title: string;
  description: ReactNode;
  /** The one thing to do next, usually a button or a link styled as one. */
  action?: ReactNode;
  /** Optional detail under the action: what it will look like, or why it is safe. */
  children?: ReactNode;
};

/**
 * What a screen shows before it has anything to show: what the screen is for, and
 * the one next step. The anatomy the experiments, surveys, pipelines and
 * implementation screens each wrote for themselves, shared so a new screen does not
 * need a fifth copy: icon, "No X yet", a sentence on what X is, one action.
 *
 * Marked `data-first-run`: a screen's product tour waits until this is gone, since the
 * state already explains the screen and the tour is about the screen once it has content.
 *
 * Copy style: plain sentences, no emoji or exclamation marks, say what the thing is
 * and what happens next.
 */
export const FirstRunState = ({ icon: Icon, title, description, action, children }: FirstRunStateProps) => (
  <section className="bg-panel border-line rounded-panel border px-4 py-10 sm:px-6 sm:py-12" data-first-run>
    <div className="mx-auto max-w-2xl text-center">
      <Icon className="text-dim mx-auto size-10" strokeWidth={1.25} />
      <h1 className="text-foreground m-0 mt-4 text-title font-medium tracking-tight">{title}</h1>
      <p className="supporting mx-auto mt-2 max-w-xl">{description}</p>
      {action && <div className="mt-5 flex justify-center">{action}</div>}
      {children && <div className="supporting mx-auto mt-6 max-w-xl text-left">{children}</div>}
    </div>
  </section>
);

type FirstRunLinkProps = { to: string; state?: Record<string, unknown>; children: string };

/** The call to action of a first-run state when it is a link rather than a button. */
export const FirstRunLink = ({ to, state, children }: FirstRunLinkProps) => (
  <NavLink
    to={to}
    state={state}
    className="bg-cta hover:bg-ctahover text-cta-foreground inline-flex h-9 items-center rounded-lg px-4 text-body"
  >
    {children}
  </NavLink>
);
